/**
 * CustomerWeeklyUpdatePanel — the per-customer "Weekly update" affordance
 * on the Customer Success cockpit.
 *
 * "Preview this week's update" renders the EXACT email the governed
 * weekly-update pipeline would send (one composer: src/lib/cs/weeklyUpdate.ts),
 * or the honest empty state when the composer returns null — no filler is
 * ever previewed or sent. "Queue for approval" creates the same
 * `send_customer_update` proposal the weekly-update agent queues, into the
 * same approvals inbox — a human approves before anything reaches the
 * outbox. No email on file → an honest "needs email" line, never a guess.
 */

import { useMemo, useState } from 'react';
import { Mail, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { C } from '../lib/tokens';
import { queueApproval } from '../lib/agent/approvals';
import { buildSendCustomerUpdateAction } from '../lib/agent/weeklyUpdateAgent';
import {
  collectWeeklyUpdateSections,
  composeWeeklyUpdate,
  resolveCustomerUpdateRecipient,
  SECTION_ORDER,
} from '../lib/cs/weeklyUpdate';
import type { Deliverable, Person, ProjectTask, Ticket } from '../lib/db/types';

export const NOTHING_TO_REPORT = 'Nothing to report this week — no filler will be sent.';

interface Props {
  person: Person;
  deliverables: Deliverable[];
  tickets: Ticket[];
  tasks: ProjectTask[];
  /** Staff gate — viewers can preview but never queue. */
  canQueue: boolean;
  /** Injectable clock for deterministic tests. */
  now?: number;
}

export function CustomerWeeklyUpdatePanel({
  person,
  deliverables,
  tickets,
  tasks,
  canQueue,
  now,
}: Props) {
  const [open, setOpen] = useState(false);
  const [queuedNote, setQueuedNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const nowMs = now ?? Date.now();
  const composed = useMemo(
    () => composeWeeklyUpdate(person, { deliverables, tickets, tasks }, nowMs),
    [person, deliverables, tickets, tasks, nowMs],
  );
  const sections = useMemo(
    () => collectWeeklyUpdateSections(person, { deliverables, tickets, tasks }, nowMs),
    [person, deliverables, tickets, tasks, nowMs],
  );
  const recipient = useMemo(() => resolveCustomerUpdateRecipient(person), [person]);

  async function onQueue() {
    if (!composed || !recipient || busy) return;
    setBusy(true);
    try {
      await queueApproval(
        buildSendCustomerUpdateAction(person, composed, recipient.email),
        'chat',
        `Weekly update to ${recipient.email} — composed from this week's real workspace data.`,
      );
      setQueuedNote('Queued for approval — review it in the approvals inbox before it sends.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: 'block', marginTop: 8 }}>
      <button
        type="button"
        className="glass-btn"
        onClick={() => setOpen((v) => !v)}
        title="Render the exact governed weekly update this customer would receive — composed only from real workspace data."
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11 }}
      >
        <Mail size={12} /> {open ? 'Hide weekly update' : "Preview this week's update"}
      </button>

      {open && (
        <span
          style={{
            display: 'block',
            marginTop: 8,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {!composed ? (
            <span className="t-meta">{NOTHING_TO_REPORT}</span>
          ) : (
            <span style={{ display: 'grid', gap: 8 }}>
              <span className="t-card-title" style={{ fontSize: 12 }}>
                {composed.subject}
              </span>
              {SECTION_ORDER.map(({ key, heading }) => {
                const items = sections[key];
                if (items.length === 0) return null; // omitted, never padded
                return (
                  <span key={key} style={{ display: 'block' }}>
                    <span className="t-eyebrow" style={{ display: 'block', marginBottom: 2 }}>
                      {heading}
                    </span>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
                      {items.map((item, i) => (
                        <li key={`${item.title}-${i}`} className="t-meta">
                          {item.title}
                          {item.detail ? ` (${item.detail})` : ''}
                        </li>
                      ))}
                    </ul>
                  </span>
                );
              })}
              <span className="t-meta">{sections.healthNote}</span>

              {queuedNote ? (
                <span
                  className="t-meta"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: C.green }}
                >
                  <CheckCircle2 size={12} /> {queuedNote}
                </span>
              ) : recipient ? (
                canQueue && (
                  <span style={{ display: 'block' }}>
                    <button
                      type="button"
                      className="glass-btn glass-btn--primary"
                      onClick={() => void onQueue()}
                      disabled={busy}
                      title={`Queue this exact email to ${recipient.email} into the approvals inbox — nothing sends without approval.`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11 }}
                    >
                      <Send size={12} /> {busy ? 'Queueing…' : 'Queue for approval'}
                    </button>
                  </span>
                )
              ) : (
                <span
                  className="t-meta"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: C.orange }}
                >
                  <AlertTriangle size={12} /> Needs email — no contact email on file. Add a POC
                  email to queue this update.
                </span>
              )}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
